import { useCallback, useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../lib/firebase';

export interface ZohoConnectionStatus {
  connectionId: string;
  provider: 'zoho';
  purpose: 'event_bridge';
  status: string;
  connectedAt: string | null;
  connectedByEmail: string | null;
  apiDomain: string | null;
  location: string | null;
  activeUserCount: number;
  reconnectCleanupRequired: boolean;
  secretCleanupRequired: boolean;
}

export interface ZohoSetupData {
  users: Array<{ id: string; name: string; email: string | null }>;
  brokers: Array<{ brokerDocId: string; name: string }>;
  mappings: Array<{
    brokerDocId: string;
    zohoOwnerId: string | null;
    zohoAssigneeId: string | null;
  }>;
}

async function callZoho<T>(body: Record<string, unknown>): Promise<T> {
  const callable = httpsCallable<Record<string, unknown>, T>(functions, 'zohoIntegrationAction');
  return (await callable(body)).data;
}

export function useZohoIntegration(companyId: string | undefined) {
  const [connection, setConnection] = useState<ZohoConnectionStatus | null>(null);
  const [loading, setLoading] = useState(Boolean(companyId));
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(() => setRefreshKey((value) => value + 1), []);

  useEffect(() => {
    if (!companyId) {
      setConnection(null);
      setLoading(false);
      return;
    }
    setConnection(null);
    setLoading(true);
    setError(null);
    let active = true;
    callZoho<{ connected: boolean; connection: ZohoConnectionStatus | null }>({
      action: 'status',
      companyId,
    })
      .then((result) => {
        if (active) setConnection(result.connection);
      })
      .catch((cause) => {
        if (active) setError(cause instanceof Error ? cause.message : String(cause));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [companyId, refreshKey]);

  const prepareOAuth = useCallback(async () => {
    if (!companyId) throw new Error('companyId required');
    const result = await callZoho<{ authoriseUrl: string }>({
      action: 'prepareOAuth',
      companyId,
    });
    window.location.assign(result.authoriseUrl);
  }, [companyId]);

  const disconnect = useCallback(async () => {
    if (!companyId) throw new Error('companyId required');
    await callZoho({ action: 'disconnect', companyId });
    refresh();
  }, [companyId, refresh]);

  const test = useCallback(async () => {
    if (!companyId) throw new Error('companyId required');
    return callZoho<{ activeUserCount: number; taskFieldCount: number }>({
      action: 'test',
      companyId,
    });
  }, [companyId]);

  const getSetupData = useCallback(async () => {
    if (!companyId) throw new Error('companyId required');
    return callZoho<ZohoSetupData>({ action: 'getSetupData', companyId });
  }, [companyId]);

  const saveMappings = useCallback(async (mappings: Array<{
    brokerDocId: string;
    zohoOwnerId: string;
    zohoAssigneeId: string | null;
  }>) => {
    if (!companyId) throw new Error('companyId required');
    await callZoho({ action: 'saveBrokerMappings', companyId, mappings });
  }, [companyId]);

  return {
    connection,
    loading,
    error,
    refresh,
    prepareOAuth,
    disconnect,
    test,
    getSetupData,
    saveMappings,
  };
}

export async function completeZohoOAuth(input: {
  companyId: string;
  code: string;
  state: string;
  accountsServer: string;
  location: string | null;
}) {
  return callZoho({ action: 'completeOAuth', ...input });
}
